<template>
  <Teleport to="body">
    <transition name="confirm-overlay" appear>
      <div v-if="modelValue" class="modal-overlay confirm-modal-overlay" @click.self="onCancel">
        <transition name="confirm-dialog" appear>
          <div class="confirm-modal" role="dialog" aria-modal="true" :aria-label="title">
        <div class="confirm-header">
          <div class="icon">
            <img src="/img/trash.svg" alt="حذف" />
          </div>
          <h3 class="title">{{ title }}</h3>
        </div>
        <div class="confirm-body">
          <slot name="message">
            <p v-html="message"></p>
          </slot>
        </div>
        <div class="confirm-actions">
          <BaseButton title="انصراف" variant="cancel" type="button" @click="onCancel" />
          <BaseButton ref="confirmBtnRef" :title="confirmText" variant="danger" type="button" @click="onConfirm" />
        </div>
          </div>
        </transition>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import BaseButton from '@/components/UiCompunents/BaseButton.vue'

const props = defineProps({
  modelValue: { type: Boolean, required: true },
  title: { type: String, default: 'تایید حذف' },
  message: { type: String, default: 'آیا از حذف این مورد اطمینان دارید؟' },
  confirmText: { type: String, default: 'بله، حذف شود' },
  cancelText: { type: String, default: 'انصراف' }
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])
const confirmBtnRef = ref(null)
watch(() => props.modelValue, async (val) => {
  if (val) {
    await nextTick()
    confirmBtnRef.value?.focus()
  }
})

function onConfirm() {
  emit('confirm')
  emit('update:modelValue', false)
}

function onCancel() {
  emit('cancel')
  emit('update:modelValue', false)
}

function onKeyDown(e) {
  if (e.key === 'Escape') onCancel()
}

onMounted(() => {
  window.addEventListener('keydown', onKeyDown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKeyDown)
})
</script>

<style scoped lang="scss">
.confirm-modal-overlay {
  position: fixed;
  inset: 0; /* top:0; right:0; bottom:0; left:0; */
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: rgba(0,0,0,0.46);
  -webkit-backdrop-filter: blur(6px) saturate(120%);
  backdrop-filter: blur(6px) saturate(120%);
  z-index: 11000; /* above typical sidebars */
}
.confirm-modal {
  width: 420px;
  max-width: 95%;
  background: var(--background-color);
  border-radius: 12px;
  box-shadow: 0 18px 30px rgba(3, 10, 18, 0.2);
  padding: 24px;
  text-align: right;
  color: var(--text-color-primary);
  border: 1px solid rgba(0,0,0,0.06);
  will-change: transform, opacity;
  transform-origin: 50% 10%;
}
.confirm-header {
  display: flex;
  /* RTL: icon should appear after the title, and the header should be right-aligned */
  flex-direction: row-reverse;
  align-items: center;
  gap: 12px;
  justify-content: flex-start; /* keep items close to the right */
  text-align: right;
}
.confirm-header .icon img {
  width: 52px;
  height: 52px;
  object-fit: cover;
}
.confirm-header .title {
  font-size: 18px;
  margin: 0;
  font-weight: 700;
  text-align: right; /* ensure title text aligns right */
}
.confirm-body p { margin: 12px 0; color: var(--text-color-thirdly); line-height: 1.5 }
.confirm-actions { display: flex; justify-content: center; gap: 12px; margin-top: 20px }
.confirm-actions :deep(.base-button) { border-radius: 8px; padding: 8px 16px; min-width: 120px; }
.confirm-actions :deep(.base-button--danger) { background: var(--background-color-danger); color: var(--text-color-button) }
.confirm-actions :deep(.base-button:focus) { outline: none; box-shadow: 0 0 0 3px rgba(20,116,242,0.12); }
.confirm-actions :deep(.base-button--danger) { border: none; box-shadow: none; outline: none; }
.confirm-actions :deep(.base-button--danger:focus) { outline: none; box-shadow: none; }

@media only screen and (max-width: 480px) {
  .confirm-modal { width: 100%; padding: 20px }
  .confirm-header .icon img { width: 40px; height: 40px }
  .confirm-actions { gap: 10px; flex-direction: column; }
  .confirm-actions .btn { min-width: 100%; }
}
.confirm-modal-overlay .confirm-modal {
  transform: translateY(0) scale(1);
  opacity: 1;
}

/* Overlay enter/leave */
.confirm-overlay-enter-from,
.confirm-overlay-leave-to {
  opacity: 0;
}
.confirm-overlay-enter-active,
.confirm-overlay-leave-active {
  transition: opacity 200ms cubic-bezier(.2, 0.9, 0.2, 1), backdrop-filter 200ms cubic-bezier(.2, 0.9, 0.2, 1);
}
.confirm-overlay-enter-to,
.confirm-overlay-leave-from {
  opacity: 1;
}

/* Dialog enter/leave: scale+translate+fade for a smooth opening */
.confirm-dialog-enter-from,
.confirm-dialog-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(.96);
}
.confirm-dialog-enter-active,
.confirm-dialog-leave-active {
  transition: transform 320ms cubic-bezier(.16,1,.3,1), opacity 320ms cubic-bezier(.16,1,.3,1);
}
.confirm-dialog-enter-to,
.confirm-dialog-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}
</style>
